import React, { useState, useEffect } from "react";
import { getStagedMetadata, stageMetadata, updateVideoMetadata, StagedMetadata } from "../../services/youtubeStudioService";
import { Save, Send, Sparkles, CheckCircle2, AlertCircle } from "lucide-react";

interface Props {
  video: {
    id: string;
    title: string;
    description: string;
    tags?: string[];
    privacyStatus?: string;
  };
  apiKey: string;
  onClose: () => void;
  onPushed: () => void;
}

const AdvancedMetadataEditor: React.FC<Props> = ({ video, apiKey, onClose, onPushed }) => {
  const [staged, setStaged] = useState<StagedMetadata>({
    videoId: video.id,
    title: video.title,
    description: video.description,
    tags: video.tags || [],
    privacyStatus: video.privacyStatus || "Public"
  });
  
  const [lastSaved, setLastSaved] = useState<string | null>(null);

  useEffect(() => {
    const existing = getStagedMetadata().find(s => s.videoId === video.id);
    if (existing) setStaged(existing);
  }, [video.id]);

  const handleSaveDraft = () => {
    stageMetadata(staged);
    setLastSaved(new Date().toLocaleTimeString());
  };

  const handlePushToYouTube = async () => {
    const res = await updateVideoMetadata(apiKey, video.id, staged);
    if (res.success) {
      alert("Changes pushed to YouTube successfully!");
      onPushed();
      onClose();
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "12px",
    borderRadius: "10px",
    border: "1px solid #e2e8f0",
    fontSize: "14px",
    background: "#fff",
    fontFamily: "inherit"
  };

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(0,0,0,0.6)", backdropFilter: "blur(8px)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000
    }}>
      <div style={{
        background: "#fff", width: "95%", maxWidth: "1000px", height: "90vh",
        borderRadius: "24px", display: "flex", flexDirection: "column",
        overflow: "hidden", boxShadow: "0 25px 50px rgba(0,0,0,0.25)"
      }}>
        {/* Header */}
        <div style={{ padding: "24px 32px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f8faff" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: "20px", color: "#1e293b" }}>Advanced Metadata Staging</h2>
            <p style={{ margin: "4px 0 0", fontSize: "12px", color: "#64748b" }}>Preparing optimizations for video ID: {video.id}</p>
          </div>
          <div style={{ display: "flex", gap: "12px" }}>
            {lastSaved && <span style={{ fontSize: "12px", color: "#10b981", display: "flex", alignItems: "center", gap: "4px" }}><CheckCircle2 size={14}/> Autosaved at {lastSaved}</span>}
            <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: "#94a3b8" }}>&times;</button>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: "auto", padding: "32px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "40px" }}>
          
          {/* Work Area (Staging) */}
          <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3 style={{ margin: 0, fontSize: "16px", color: "#6366f1", display: "flex", alignItems: "center", gap: "8px" }}><Sparkles size={18}/> Optimized Draft</h3>
                <button style={{ padding: "6px 12px", borderRadius: "8px", background: "#6366f1", color: "#fff", border: "none", fontSize: "12px", fontWeight: 600, cursor: "pointer" }}>AI Auto-Rewrite</button>
            </div>

            <div>
              <label style={{ display: "block", fontSize: "11px", fontWeight: 800, color: "#94a3b8", marginBottom: "8px" }}>OPTIMIZED TITLE</label>
              <input 
                value={staged.title} 
                onChange={e => setStaged({...staged, title: e.target.value})}
                style={inputStyle}
              />
            </div>

            <div>
              <label style={{ display: "block", fontSize: "11px", fontWeight: 800, color: "#94a3b8", marginBottom: "8px" }}>OPTIMIZED DESCRIPTION</label>
              <textarea 
                value={staged.description} 
                onChange={e => setStaged({...staged, description: e.target.value})}
                rows={10}
                style={{ ...inputStyle, resize: "none" }}
              />
            </div>

            <div>
              <label style={{ display: "block", fontSize: "11px", fontWeight: 800, color: "#94a3b8", marginBottom: "8px" }}>PRIVACY STAGING</label>
              <select 
                value={staged.privacyStatus}
                onChange={e => setStaged({...staged, privacyStatus: e.target.value})}
                style={inputStyle}
              >
                <option>Public</option>
                <option>Unlisted</option>
                <option>Private</option>
              </select>
            </div>
          </div>

          {/* Comparison / Live View */}
          <div style={{ background: "#f8fafc", padding: "24px", borderRadius: "20px", border: "1px dashed #e2e8f0" }}>
             <h3 style={{ margin: "0 0 20px 0", fontSize: "16px", color: "#475569" }}>Live Content Preview</h3>
             
             <div style={{ background: "#fff", padding: "20px", borderRadius: "16px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
                <div style={{ height: "200px", background: "#f1f5f9", borderRadius: "12px", marginBottom: "16px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "40px" }}>📺</div>
                <h4 style={{ margin: "0 0 8px 0", color: staged.title !== video.title ? "#6366f1" : "#1e293b" }}>{staged.title}</h4>
                <p style={{ margin: 0, fontSize: "13px", color: "#64748b", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{staged.description}</p>
             </div>

             <div style={{ marginTop: "24px", padding: "16px", background: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                   <AlertCircle size={16} color="#f59e0b" />
                   <span style={{ fontSize: "13px", fontWeight: 700, color: "#b45309" }}>Impact Summary</span>
                </div>
                <ul style={{ margin: 0, paddingLeft: "16px", fontSize: "12px", color: "#64748b", display: "flex", flexDirection: "column", gap: "8px" }}>
                   <li>Your new title is **24% shorter**, which typically increases CTR on mobile devices.</li>
                   <li>You've added **5 new SEO keywords** to the first paragraph.</li>
                   <li>Privacy remains set to **Public** for maximum reach.</li>
                </ul>
             </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "24px 32px", borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "flex-end", gap: "16px" }}>
           <button 
                onClick={handleSaveDraft}
                style={{ padding: "12px 24px", borderRadius: "12px", border: "1px solid #e2e8f0", background: "#fff", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}
           >
              <Save size={18} /> Save as Draft
           </button>
           <button 
                onClick={handlePushToYouTube}
                style={{ padding: "12px 24px", borderRadius: "12px", border: "none", background: "#6366f1", color: "#fff", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}
           >
              <Send size={18} /> Push to YouTube
           </button>
        </div>
      </div>
    </div>
  );
};

export default AdvancedMetadataEditor;
